/*
 * <loox-store-rating> -- one-line store-wide Loox rating.
 *
 * Markup contract (see snippets/store-rating.liquid):
 *   <loox-store-rating data-store-id data-locale data-t-one data-t-other hidden>
 *     <span data-store-rating-text>   {# "4.8 / 5 store rating from 2,140 reviews" #}
 *
 * Kept apart from loox-reviews.js on purpose: the cart drawer mounts this on
 * every page, so it has to stay tiny. The fetch is lazy (first time the element
 * is on screen - a closed cart drawer never triggers it) and the bottomline is
 * shared by every instance on the page and cached per tab session, so cart
 * re-renders and page views after the first cost nothing.
 */
(function () {
  if (customElements.get('loox-store-rating')) return;

  var CACHE_KEY = 'ucon:loox-store-bottomline';
  var CACHE_TTL = 60 * 60 * 1000;
  var pending = null;

  function readCache(storeId) {
    try {
      var hit = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (hit && hit.storeId === storeId && Date.now() - hit.at < CACHE_TTL) return hit.data;
    } catch (e) {}
    return null;
  }

  function writeCache(storeId, data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ storeId: storeId, at: Date.now(), data: data }));
    } catch (e) {}
  }

  // No productId on this path = the store-wide bottomline (same call as the
  // homepage reviews strip in loox-reviews.js).
  function bottomline(storeId) {
    var cached = readCache(storeId);
    if (cached) return Promise.resolve(cached);
    if (!pending) {
      var url = 'https://storefront-api.loox.io/storefront/v1/store/' +
        encodeURIComponent(storeId) + '/product-reviews/bottomline';
      pending = fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error(url + ' -> ' + res.status);
          return res.json();
        })
        .then(function (data) {
          writeCache(storeId, data);
          return data;
        })
        .catch(function (e) {
          pending = null;
          throw e;
        });
    }
    return pending;
  }

  customElements.define(
    'loox-store-rating',
    class extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.storeId || this.filled) return;

        // A warm cache renders straight away - no observer, no flash.
        var cached = readCache(this.dataset.storeId);
        if (cached) return this.render(cached);

        if (!('IntersectionObserver' in window)) return this.load();

        // [hidden] has no box to intersect, so observe the nearest rendered
        // ancestor instead. Inside a closed <dialog> that ancestor is not
        // rendered either, so the fetch waits until the drawer opens.
        var target = this.parentElement || this;
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          this.disconnectObserver();
          this.load();
        }, { rootMargin: '200px' });
        this.observer.observe(target);
      }

      disconnectedCallback() {
        this.disconnectObserver();
      }

      disconnectObserver() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
      }

      load() {
        bottomline(this.dataset.storeId)
          .then((data) => this.render(data))
          .catch(function (e) { console.warn('[loox-store-rating]', e); });
      }

      render(data) {
        var total = Number(data && data.totalReviews) || 0;
        var average = Number(data && data.averageRating) || 0;
        var text = this.querySelector('[data-store-rating-text]');
        if (!total || !average || !text) return;

        var locale = this.dataset.locale || undefined;
        var rating = average.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        var template = total === 1
          ? this.dataset.tOne || '%RATING% / 5 store rating from %COUNT% review'
          : this.dataset.tOther || '%RATING% / 5 store rating from %COUNT% reviews';

        text.textContent = template
          .replace('%RATING%', rating)
          .replace('%COUNT%', total.toLocaleString(locale));
        this.filled = true;
        this.hidden = false;
      }
    }
  );
})();
